import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, Drawer, Tooltip } from 'antd';
import { TeamOutlined, RightOutlined, DownOutlined, InfoCircleFilled, CloseOutlined, CalendarOutlined, PlusOutlined, MoreOutlined } from '@ant-design/icons';
import CommonTable from '../../components/commonTable';
import Calendar from '../../components/calendar';
import ColumnGroupSelect from '../../components/columnGroupSelect';
import TeamMemberSelect from '../../components/teamMemberSelect';
import { useGetLeadGroupCountsQuery } from '../../services/leadGroupCounts';
import { useGetTeamDashboardQuery } from '../../services/leads';
import { SUBTEAMS, SubteamRow } from './subteamsData';
import { SubteamDetailRoute } from '../../routes/routepath';
import './styles.scss';

const { TabPane } = Tabs;

// Team dashboard date presets (set concrete ranges).
const startOfDay = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const daysAgo = (n: number) => { const d = new Date(); d.setDate(d.getDate() - n); return startOfDay(d); };
const RANGE_PRESETS = [
  { label: 'Last 24 hours', getRange: () => [daysAgo(1), startOfDay(new Date())] as [Date, Date] },
  { label: 'Last 7 days',   getRange: () => [daysAgo(6), startOfDay(new Date())] as [Date, Date] },
  { label: 'Last 30 days',  getRange: () => [daysAgo(29), startOfDay(new Date())] as [Date, Date] },
  { label: 'Last 90 days',  getRange: () => [daysAgo(89), startOfDay(new Date())] as [Date, Date] },
  { label: 'All Time',      getRange: () => [new Date(2000, 0, 1), startOfDay(new Date())] as [Date, Date] },
];

const fmt = (d: Date) => `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
const fmtISO = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/* ─── Types ──────────────────────────────────────────────────────────── */

interface MemberRow {
  key:             string;
  rank:            number | null;   // null => TOTAL row
  name:            string;
  email:           string;
  assigned:        number;
  contacted:       number;
  contactedPercent: number;
  avgResponse:     string;
  totalActivities: number;
  group:           string;
}

const TABS = ['Team Dashboard', 'Team Members', 'Subteams', 'Lead Assignment'];

// Pull a field from an object trying several possible key names (API shape flexible).
const pick = (o: any, ...keys: string[]) => {
  for (const k of keys) if (o && o[k] !== undefined && o[k] !== null) return o[k];
  return undefined;
};

/* ─── Component ───────────────────────────────────────────────────────── */

const LeadTeam = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Team Dashboard');
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  const [range, setRange] = useState<{ start: Date; end: Date; label?: string }>(() => {
    const [s, e] = RANGE_PRESETS[1].getRange();
    return { start: s, end: e, label: 'Last 7 days' };
  });
  const [selectedGroup, setSelectedGroup] = useState<{ id: string; name: string } | null>(null);
  const [colOpen, setColOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const dateRef = useRef<HTMLDivElement>(null);
  const membersRef = useRef<HTMLDivElement>(null);

  // Close the members popup on outside click.
  useEffect(() => {
    if (!membersOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (membersRef.current && !membersRef.current.contains(e.target as Node)) setMembersOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [membersOpen]);

  // Close the calendar popup on outside click.
  useEffect(() => {
    if (!calOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (dateRef.current && !dateRef.current.contains(e.target as Node)) setCalOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [calOpen]);

  // Groups with assigned counts (GET /lead-groups/counts).
  const { data: groupsResp, isFetching: groupsLoading } = useGetLeadGroupCountsQuery(undefined, { skip: !groupsOpen });
  const groupList = ((groupsResp?.data ?? groupsResp ?? []) as any[]).map((g, i) => ({
    id:    g._id ?? g.id ?? `g-${i}`,
    name:  g.name ?? '—',
    color: g.colour ?? g.color ?? '#64748b',
    count: g.leadCount ?? g.clientsAssigned ?? g.count ?? 0,
  }));

  // ── Team dashboard data (GET /leads/team-dashboard) ──
  const { data: dashResp, isFetching: dashLoading } = useGetTeamDashboardQuery({
    startDate: fmtISO(range.start),
    endDate:   fmtISO(range.end),
    groupId:   selectedGroup?.id || undefined,
  });

  const dash: any = (dashResp as any)?.data ?? dashResp ?? {};
  const summary: any = dash.summary ?? dash;

  const kpis = [
    { key: 'members',   label: 'Team Members',          value: `${pick(summary, 'teamMembers', 'totalMembers') ?? 0}`,       sub: 'Activated accounts',            tip: 'Total number of members on your team' },
    { key: 'assigned',  label: 'Assigned Clients',      value: `${pick(summary, 'assignedClients') ?? 0}`,                   sub: `of ${pick(summary, 'totalClients') ?? 0} added`, tip: 'Total number of new leads who were assigned during this time Period' },
    { key: 'contacted', label: 'Contacted Clients',     value: `${pick(summary, 'contactedClients') ?? 0}`,                  sub: `${pick(summary, 'contactedPercent') ?? 0}% of clients assigned`, tip: 'Total number of Assigned Clients who were contacted' },
    { key: 'response',  label: 'Average Response Time', value: `${pick(summary, 'avgResponseTime', 'averageResponseTime') ?? '-'}`, sub: 'for contacted clients', tip: 'Average time taken to log first response for all Contacted Clients' },
  ];

  const rawMembers: any[] = pick(dash, 'members', 'leaderboard', 'rows') ?? [];
  const memberRows: MemberRow[] = rawMembers.map((m, i) => ({
    key:             pick(m, 'userId', '_id', 'email') ?? `m-${i}`,
    rank:            i + 1,
    name:            pick(m, 'name', 'fullName') ?? '—',
    email:           pick(m, 'email') ?? '',
    assigned:        Number(pick(m, 'assignedClients') ?? 0),
    contacted:       Number(pick(m, 'contactedClients') ?? 0),
    contactedPercent: Number(pick(m, 'contactedPercent') ?? 0),
    avgResponse:     `${pick(m, 'avgResponseTime', 'averageResponseTime') ?? '-'}`,
    totalActivities: Number(pick(m, 'totalActivities') ?? 0),
    group:           pick(m, 'group', 'groupName') ?? '-',
  }));

  const totalsAssigned   = pick(summary, 'assignedClients') ?? memberRows.reduce((s, m) => s + m.assigned, 0);
  const totalsContacted  = pick(summary, 'contactedClients') ?? memberRows.reduce((s, m) => s + m.contacted, 0);
  const totalsActivities = pick(summary, 'totalActivities') ?? memberRows.reduce((s, m) => s + m.totalActivities, 0);

  const visibleMembers = selectedMembers.length
    ? memberRows.filter(m => selectedMembers.includes(m.key))
    : memberRows;

  const dataSource: MemberRow[] = [
    { key: 'total', rank: null, name: 'Total', email: '', assigned: totalsAssigned, contacted: totalsContacted, contactedPercent: 0, avgResponse: `${pick(summary, 'avgResponseTime', 'averageResponseTime') ?? '-'}`, totalActivities: totalsActivities, group: '-' },
    ...visibleMembers,
  ];

  const columns = [
    {
      title: 'TEAM MEMBER',
      dataIndex: 'name',
      key: 'name',
      width: 260,
      render: (_: unknown, r: MemberRow) =>
        r.rank === null ? (
          <span className="lt-total-label">Total</span>
        ) : (
          <div className="lt-member">
            <span className="lt-rank">{r.rank}</span>
            <div>
              <div className="lt-member-name">{r.name}</div>
              <div className="lt-member-email">{r.email}</div>
            </div>
          </div>
        ),
    },
    { title: 'ASSIGNED CLIENTS', dataIndex: 'assigned', key: 'assigned', width: 140, align: 'left' as const },
    {
      title: 'CONTACTED CLIENTS',
      dataIndex: 'contacted',
      key: 'contacted',
      width: 150,
      render: (v: number, r: MemberRow) => (
        <div>
          <div>{v}</div>
          {r.rank !== null && (
            <div className="lt-sub">{r.contactedPercent}% of assigned</div>
          )}
        </div>
      ),
    },
    { title: 'AVERAGE RESPONSE TIME', dataIndex: 'avgResponse', key: 'avgResponse', width: 170 },
    { title: 'TOTAL ACTIVITIES', dataIndex: 'totalActivities', key: 'totalActivities', width: 140 },
    {
      title: (
        <span className={`lt-in-col ${(colOpen || selectedGroup) ? 'active' : ''}`}>
          IN <ColumnGroupSelect value={selectedGroup} onChange={setSelectedGroup} onOpenChange={setColOpen} />
        </span>
      ),
      dataIndex: 'group',
      key: 'group',
      width: 170,
      onHeaderCell: () => ({
        style: (colOpen || selectedGroup) ? { background: 'var(--accent)', color: '#0a1f13' } : {},
      }) as any,
    },
  ];

  const subteamColumns = [
    {
      title: (
        <span className="lt-col-title">
          NAME <DownOutlined style={{ fontSize: 11 }} />
        </span>
      ),
      dataIndex: 'name',
      key: 'name',
      render: (text: string, _record: SubteamRow, index: number) => (
        <div className="lt-subteam-name-cell">
          <span className="lt-subteam-index">{index + 1}</span>
          <TeamOutlined className="lt-subteam-icon" />
          <span className="lt-subteam-name">{text}</span>
        </div>
      ),
    },
    {
      title: 'MEMBERS',
      dataIndex: 'members',
      key: 'members',
      render: (v: number) => `${v} members`,
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      align: 'right' as const,
      render: () => <MoreOutlined className="lt-subteam-more" />,
    },
  ];

  return (
    <div className="lt-page">
      <h1 className="lt-title">Team</h1>

      <div className="lt-tabs">
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          {TABS.map(t => <TabPane tab={t} key={t} />)}
        </Tabs>
      </div>

      {activeTab === 'Team Dashboard' ? (
        <div className="lt-body">
          {/* ── Filter pills ── */}
          <div className="lt-filters">
            <button className="lt-filter-pill" onClick={() => setGroupsOpen(true)}>
              <span className="lt-pill-left"><TeamOutlined /> {selectedGroup?.name || 'All Groups'}</span>
              <RightOutlined className="lt-pill-caret" />
            </button>

            <div className="lt-members" ref={membersRef}>
              <button className="lt-filter-pill" onClick={() => setMembersOpen(o => !o)}>
                <span className="lt-pill-left">
                  <TeamOutlined /> {selectedMembers.length ? `${selectedMembers.length} Member${selectedMembers.length > 1 ? 's' : ''}` : 'All Members'}
                </span>
                <RightOutlined className="lt-pill-caret" />
              </button>
              {membersOpen && (
                <div className="lt-members-popup">
                  <TeamMemberSelect
                    open={membersOpen}
                    onClose={() => setMembersOpen(false)}
                    onSelect={(ids: string[]) => { setSelectedMembers(ids); setMembersOpen(false); }}
                    selectedMembers={selectedMembers}
                  />
                </div>
              )}
            </div>
            <div className="lt-daterange" ref={dateRef}>
              <button className="lt-filter-pill" onClick={() => setCalOpen(o => !o)}>
                <span className="lt-pill-left">
                  <CalendarOutlined /> <strong>{range.label || 'Custom'}</strong>
                  <span className="lt-muted">{fmt(range.start)} - {fmt(range.end)}</span>
                </span>
                <DownOutlined className="lt-pill-caret" />
              </button>
              {calOpen && (
                <div className="lt-cal-popup" onClick={e => e.stopPropagation()}>
                  <Calendar
                    selectedDate={{ start: range.start, end: range.end }}
                    rangePresets={RANGE_PRESETS}
                    onDateChange={(v: any) => {
                      if (v?.start && v?.end) {
                        setRange({ start: new Date(v.start), end: new Date(v.end), label: v.label });
                        setCalOpen(false);
                      }
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* ── KPI cards ── */}
          <div className="lt-kpis">
            {kpis.map(k => (
              <div className="lt-kpi" key={k.key}>
                <div className="lt-kpi-head">
                  <span className="lt-kpi-label">{k.label}</span>
                  <Tooltip title={k.tip} placement="top">
                    <InfoCircleFilled className="lt-kpi-info" />
                  </Tooltip>
                </div>
                <div className="lt-kpi-value">{k.value}</div>
                <div className="lt-kpi-sub">{k.sub}</div>
              </div>
            ))}
          </div>

          {/* ── Leaderboard ── */}
          <CommonTable
            columns={columns}
            dataSource={dataSource}
            loading={dashLoading}
            rowKey="key"
            scroll={{ x: 900 }}
            rowClassName={(r: MemberRow) => (r.rank === null ? 'lt-total-row' : '')}
            className="lt-table"
          />
        </div>
      ) : activeTab === 'Subteams' ? (
        <div className="lt-subteams">
          <div className="lt-subteams-head">
            <p className="lt-subteams-desc">
              Create subteams to organise your team members and have better control over what clients they have access to.
            </p>
            <button className="lt-create-subteam-btn">
              <PlusOutlined /> CREATE SUBTEAM
            </button>
          </div>

          <div className="lt-subteams-count">{SUBTEAMS.length} SUBTEAMS</div>

          <CommonTable
            columns={subteamColumns}
            dataSource={SUBTEAMS}
            rowKey="key"
            pagination={false}
            className="lt-subteams-table"
            onRow={(record: SubteamRow) => ({
              onClick: () => navigate(`${SubteamDetailRoute}/${record.key}`),
              style: { cursor: 'pointer' },
            })}
          />

          <div className="lt-subteams-empty">
            <span className="lt-subteams-empty-icon">
              <TeamOutlined />
            </span>
            <h3 className="lt-subteams-empty-title">Welcome to your Subteams</h3>
            <p className="lt-subteams-empty-text">
              Organize your team into subteams to control who can view specific clients more effectively
            </p>
          </div>
        </div>
      ) : (
        <div className="lt-placeholder">{activeTab} — coming soon.</div>
      )}

      {/* ── Groups slider ── */}
      <Drawer
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0, fontSize: 28, fontWeight: 800, color: 'var(--sider-text)' }}>Groups</h2>
            <CloseOutlined style={{ fontSize: 20, cursor: 'pointer', color: '#999' }} onClick={() => setGroupsOpen(false)} />
          </div>
        }
        placement="right"
        onClose={() => setGroupsOpen(false)}
        open={groupsOpen}
        width={420}
        closable={false}
        className="lt-groups-drawer"
        styles={{
          body:    { background: 'var(--card-bg)' },
          header:  { background: 'var(--card-bg)' },
          content: { background: 'var(--card-bg)' },
        } as any}
      >
        {groupsLoading && <div className="lt-groups-state">Loading…</div>}
        {!groupsLoading && (
          <div
            className={`lt-group-row ${!selectedGroup ? 'active' : ''}`}
            onClick={() => { setSelectedGroup(null); setGroupsOpen(false); }}
          >
            <span className="lt-group-color" style={{ background: 'var(--muted)' }} />
            <span className="lt-group-name">All Groups</span>
          </div>
        )}
        {!groupsLoading && groupList.map(g => (
          <div
            className={`lt-group-row ${selectedGroup?.id === g.id ? 'active' : ''}`}
            key={g.id}
            onClick={() => { setSelectedGroup({ id: g.id, name: g.name }); setGroupsOpen(false); }}
          >
            <span className="lt-group-color" style={{ background: g.color }} />
            <span className="lt-group-name">{g.name}</span>
            <span className="lt-group-count">{g.count}</span>
          </div>
        ))}
      </Drawer>
    </div>
  );
};

export default LeadTeam;
